/* Cloud 9 Slime — Magic Effects Engine (Stage 3: Full Sensory) */
(() => {
  'use strict';

  const isMouse = () => !window.matchMedia('(pointer: coarse)').matches;
  const wantsMotion = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── SCROLL PROGRESS BAR ── */
  function initScrollProgress() {
    const bar = document.createElement('span');
    bar.className = 'c9-scroll-progress';
    document.body.prepend(bar);
    const update = () => {
      const max = document.body.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      bar.style.setProperty('--progress', `${((window.scrollY / max) * 100).toFixed(2)}%`);
    };
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── CURSOR SPARKLE TRAIL ── */
  function initCursorSparkles() {
    if (!isMouse() || !wantsMotion()) return;
    const colors = ['#d946a8', '#9333ea', '#e571cb', '#b44cd4', '#7c3aed'];
    let lastTime = 0;

    document.addEventListener('mousemove', (e) => {
      const now = Date.now();
      if (now - lastTime < 35) return;
      lastTime = now;
      if (Math.random() > 0.65) return;

      const el = document.createElement('span');
      const size = Math.round(Math.random() * 10 + 7);
      const color = colors[Math.floor(Math.random() * colors.length)];
      const dx = ((Math.random() - 0.5) * 54).toFixed(0);
      const dy = (-(Math.random() * 48 + 18)).toFixed(0);

      el.style.cssText =
        'position:fixed' +
        ';left:' + e.clientX + 'px' +
        ';top:' + e.clientY + 'px' +
        ';width:' + size + 'px' +
        ';height:' + size + 'px' +
        ';background-color:' + color +
        ';border-radius:50%' +
        ';pointer-events:none' +
        ';z-index:2147483647' +
        ';opacity:1' +
        ';box-shadow:0 0 8px 3px ' + color +
        ';transform:translate(-50%,-50%) scale(1)' +
        ';transition:transform 0.65s ease-out,opacity 0.65s ease-out';

      document.body.appendChild(el);

      /* Double rAF ensures the initial state is painted before transition starts */
      requestAnimationFrame(() => requestAnimationFrame(() => {
        el.style.transform = 'translate(calc(-50% + ' + dx + 'px),calc(-50% + ' + dy + 'px)) scale(0)';
        el.style.opacity = '0';
      }));

      setTimeout(() => el.remove(), 700);
    });
  }

  /* ── 3D CARD TILT + MOUSE SHINE ── */
  function initCardTilt() {
    if (!isMouse() || !wantsMotion()) return;

    const observe = (wrapper) => {
      const card = wrapper.querySelector('.c9-card');
      if (!card) return;

      wrapper.addEventListener('mousemove', (e) => {
        const r = wrapper.getBoundingClientRect();
        const cx = r.width / 2;
        const cy = r.height / 2;
        const dx = e.clientX - r.left - cx;
        const dy = e.clientY - r.top - cy;
        wrapper.style.setProperty('--tilt-x', `${(-(dy / cy) * 7).toFixed(2)}deg`);
        wrapper.style.setProperty('--tilt-y', `${((dx / cx) * 10).toFixed(2)}deg`);
        card.style.setProperty('--shine-x', `${((e.clientX - r.left) / r.width * 100).toFixed(1)}%`);
        card.style.setProperty('--shine-y', `${((e.clientY - r.top) / r.height * 100).toFixed(1)}%`);
      });

      wrapper.addEventListener('mouseleave', () => {
        wrapper.style.setProperty('--tilt-x', '0deg');
        wrapper.style.setProperty('--tilt-y', '0deg');
        card.style.removeProperty('--shine-x');
        card.style.removeProperty('--shine-y');
      });
    };

    document.querySelectorAll('.c9-card-wrapper').forEach(observe);
  }

  /* ── STAGGERED GRID ENTRANCE ── */
  function initGridReveal() {
    const items = document.querySelectorAll('.product-grid .c9-card-wrapper');
    if (!items.length) return;

    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter(e => e.isIntersecting)
        .sort((a, b) => {
          const dy = a.boundingClientRect.top - b.boundingClientRect.top;
          return dy !== 0 ? dy : a.boundingClientRect.left - b.boundingClientRect.left;
        });

      visible.forEach((entry, i) => {
        const el = entry.target;
        if (wantsMotion()) {
          el.style.setProperty('--c9-stagger', `${i * 150}ms`);
        }
        requestAnimationFrame(() => el.classList.add('c9-visible'));
        observer.unobserve(el);
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -60px 0px' });

    items.forEach(el => observer.observe(el));
  }

  /* ── GENERIC REVEAL ── */
  function initRevealEls() {
    const els = document.querySelectorAll('.c9-reveal');
    if (!els.length) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -20px 0px' });

    els.forEach(el => obs.observe(el));
  }

  /* ── BUTTON RIPPLE ── */
  function initRipple() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.c9-card__cta, .product-form__submit, .button--primary');
      if (!btn || btn.disabled || btn.classList.contains('c9-card__cta--sold-out')) return;
      const rect = btn.getBoundingClientRect();
      const ripple = document.createElement('span');
      ripple.className = 'c9-ripple';
      ripple.style.left = `${e.clientX - rect.left}px`;
      ripple.style.top  = `${e.clientY - rect.top}px`;
      btn.appendChild(ripple);
      ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
    });
  }

  /* ── FLOATING BANNER SPARKLES ── */
  function initBannerSparkles() {
    if (!isMouse() || !wantsMotion()) return;
    const hero = document.querySelector('.collection-hero');
    if (!hero) return;

    const chars  = ['✦', '✧', '✨', '★', '✽', '✸', '✺'];
    const colors = ['#d946a8', '#9333ea', '#e571cb', '#b44cd4', '#f0a0e0'];

    const emit = () => {
      const el = document.createElement('span');
      el.className = 'c9-banner-sparkle';
      el.textContent = chars[Math.floor(Math.random() * chars.length)];
      el.style.cssText = [
        `left:${(Math.random() * 90 + 5).toFixed(1)}%`,
        `top:${(Math.random() * 68 + 8).toFixed(1)}%`,
        `--size:${(Math.random() * 14 + 10).toFixed(0)}px`,
        `--color:${colors[Math.floor(Math.random() * colors.length)]}`,
        `--dur:${(Math.random() * 1.4 + 1.4).toFixed(2)}s`,
      ].join(';');
      hero.appendChild(el);
      el.addEventListener('animationend', () => el.remove(), { once: true });
    };

    for (let i = 0; i < 8; i++) setTimeout(emit, i * 100);
    setInterval(emit, 360);
  }

  /* ── INIT ── */
  function initAll() {
    initScrollProgress();
    initCursorSparkles();
    initCardTilt();
    initGridReveal();
    initRevealEls();
    initRipple();
    initBannerSparkles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
